import React, { useState, useEffect, useRef } from 'react';
import { AdminHeader } from './AdminLayout';
import { 
    getInstrumente, 
    getInstrumentengruppen, 
    createInstrument, 
    updateInstrument, 
    deleteInstrument,
    createInstrumentengruppe,
    updateInstrumentengruppe,
    deleteInstrumentengruppe,
    getErrorMessage
} from '@/lib/api';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { 
    Plus, Pencil, Trash2, Music, Search, ChevronDown, ChevronRight,
    Mic, Piano, Guitar, Drum, Wind, GripVertical, FolderSync, FileDown, FileUp, Download, FileSpreadsheet, Upload, CheckCircle2, AlertCircle, Database
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Available emojis for instrument groups
const AVAILABLE_GROUP_EMOJIS = [
    { name: '🎵', label: 'Note (Standard)' },
    { name: '🎹', label: 'Klavier/Tasten' },
    { name: '🎸', label: 'Gitarre/Saiten' },
    { name: '🎺', label: 'Blechblasinstrumente' },
    { name: '🎷', label: 'Holzblasinstrumente' },
    { name: '🥁', label: 'Schlagwerk' },
    { name: '🎤', label: 'Gesang' },
    { name: '🎻', label: 'Streichinstrumente' },
    { name: '🪗', label: 'Akkordeon' },
    { name: '🎶', label: 'Musik' },
    { name: '👶', label: 'Für die Kleinen' },
    { name: '✨', label: 'Elementar' },
];

// Get emoji for group (default: 🎵)
const getGroupEmoji = (emojiName) => {
    if (!emojiName) return '🎵';
    const found = AVAILABLE_GROUP_EMOJIS.find(e => e.name === emojiName);
    return found ? found.name : emojiName;
};

// Available icons for instruments (Lucide)
const AVAILABLE_ICONS = [
    { name: 'Music', icon: Music, label: 'Note (Standard)' },
    { name: 'Mic', icon: Mic, label: 'Mikrofon (Gesang)' },
    { name: 'Piano', icon: Piano, label: 'Klavier' },
    { name: 'Guitar', icon: Guitar, label: 'Gitarre' },
    { name: 'Drum', icon: Drum, label: 'Schlagzeug' },
    { name: 'Wind', icon: Wind, label: 'Blasinstrument' },
];

// Get icon component by name
const getIconComponent = (iconName, defaultIcon = Music) => {
    const found = AVAILABLE_ICONS.find(i => i.name === iconName);
    return found ? found.icon : defaultIcon;
};

// Sortable Group Item Component
function SortableGruppeItem({ gruppe, isExpanded, instrumentCount, instrumente, onToggle, onEdit, onDelete, onEditInstrument, onDeleteInstrument }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: gruppe.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    // Get emoji for group
    const gruppeEmoji = getGroupEmoji(gruppe.icon);

    return (
        <div ref={setNodeRef} style={style}>
            <Collapsible open={isExpanded} onOpenChange={() => onToggle(gruppe.id)}>
                <div className={cn("bg-card rounded-2xl border overflow-hidden", isDragging && "ring-2 ring-primary")} data-testid={`gruppe-${gruppe.id}`}>
                    {/* Group Header */}
                    <div className="flex items-center">
                        {/* Drag Handle */}
                        <div
                            {...attributes}
                            {...listeners}
                            className="pl-3 pr-1 py-4 cursor-grab active:cursor-grabbing hover:bg-muted/30 rounded-l-2xl"
                            title="Ziehen zum Sortieren"
                        >
                            <GripVertical className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <CollapsibleTrigger asChild>
                            <button className="flex-1 px-3 py-4 flex items-center gap-3 hover:bg-muted/30 transition-colors text-left">
                                {isExpanded ? (
                                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                                ) : (
                                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                                )}
                                <span className="text-2xl">{gruppeEmoji}</span>
                                <div className="flex-1">
                                    <span className="text-lg font-semibold">{gruppe.name}</span>
                                    <span className="text-base text-muted-foreground ml-3">
                                        {instrumentCount} {instrumentCount === 1 ? 'Instrument' : 'Instrumente'}
                                    </span>
                                </div>
                            </button>
                        </CollapsibleTrigger>
                        <div className="flex items-center gap-1 pr-4">
                            <button 
                                onClick={(e) => { e.stopPropagation(); onEdit(gruppe); }} 
                                className="btn-icon"
                                title="Gruppe bearbeiten"
                            >
                                <Pencil className="h-4 w-4" />
                            </button>
                            <button 
                                onClick={(e) => { e.stopPropagation(); onDelete(gruppe); }} 
                                className="btn-icon btn-icon-destructive"
                                title="Gruppe löschen"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                    
                    {/* Instruments List */}
                    <CollapsibleContent>
                        <div className="border-t">
                            {instrumente.length === 0 ? (
                                <div className="px-5 py-4 text-muted-foreground text-center">
                                    Keine Instrumente in dieser Gruppe
                                </div>
                            ) : (
                                instrumente.map((item, idx) => {
                                    const IconComponent = getIconComponent(item.icon, Music);
                                    return (
                                        <div 
                                            key={item.id}
                                            className={cn(
                                                "px-5 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors",
                                                idx !== instrumente.length - 1 && "border-b"
                                            )}
                                            data-testid={`instrument-${item.id}`}
                                        >
                                            <div className="flex items-center gap-3 ml-8">
                                                <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                                                    <IconComponent className="h-4 w-4 text-violet-600" />
                                                </div>
                                                <span className="text-base font-medium">{item.name}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <button 
                                                    onClick={() => onEditInstrument(item)} 
                                                    className="btn-icon"
                                                    title="Instrument bearbeiten"
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                                <button 
                                                    onClick={() => onDeleteInstrument(item)} 
                                                    className="btn-icon btn-icon-destructive"
                                                    title="Instrument löschen"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </CollapsibleContent>
                </div>
            </Collapsible>
        </div>
    );
}

export function InstrumenteUndGruppen() {
    const [instrumente, setInstrumente] = useState([]);
    const [gruppen, setGruppen] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    
    // Dialog states
    const [instrumentDialogOpen, setInstrumentDialogOpen] = useState(false);
    const [gruppeDialogOpen, setGruppeDialogOpen] = useState(false);
    const [editInstrument, setEditInstrument] = useState(null);
    const [editGruppe, setEditGruppe] = useState(null);
    const [deleteItem, setDeleteItem] = useState(null);
    const [deleteType, setDeleteType] = useState(null); // 'instrument' or 'gruppe'
    
    // Form data
    const [instrumentFormData, setInstrumentFormData] = useState({ name: '', gruppe_id: '', icon: '' });
    const [gruppeFormData, setGruppeFormData] = useState({ name: '', icon: '' });
    
    // Import/Export
    const [dataDialogOpen, setDataDialogOpen] = useState(false);
    const [dataDialogTab, setDataDialogTab] = useState('export');
    const [jsonImportFile, setJsonImportFile] = useState(null);
    const [jsonImporting, setJsonImporting] = useState(false);
    const [jsonImportResult, setJsonImportResult] = useState(null);
    const jsonInputRef = useRef(null);

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const fetchData = async () => {
        try {
            setLoading(true);
            const [instrumenteRes, gruppenRes] = await Promise.all([
                getInstrumente(),
                getInstrumentengruppen()
            ]);
            setInstrumente(instrumenteRes.data);
            // Groups already sorted by backend
            setGruppen(gruppenRes.data);
            // Keep all groups collapsed by default
            setExpandedGroups(new Set());
        } catch (error) {
            toast.error('Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    };

    // === IMPORT/EXPORT FUNCTIONS ===
    const handleJSONExport = () => {
        const exportData = {
            instrumente: instrumente.map(item => ({
                id: item.id,
                name: item.name,
                gruppe_id: item.gruppe_id,
                icon: item.icon
            })),
            gruppen: gruppen.map(g => ({
                id: g.id,
                name: g.name,
                icon: g.icon,
                sort_order: g.sort_order
            }))
        };
        
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `instrumente_backup_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${instrumente.length} Instrumente und ${gruppen.length} Gruppen exportiert`);
    };

    const handleCSVExport = () => {
        const headers = ['Name', 'Gruppe'];
        const rows = instrumente.map(item => [
            item.name || '',
            gruppen.find(g => g.id === item.gruppe_id)?.name || ''
        ]);
        
        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
            .join('\n');
        
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `instrumente_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${instrumente.length} Instrumente als CSV exportiert`);
    };

    const handleJSONImport = async () => {
        if (!jsonImportFile) return;
        
        try {
            setJsonImporting(true);
            setJsonImportResult(null);
            
            const text = await jsonImportFile.text();
            const data = JSON.parse(text);
            
            let importedInstr = 0;
            let updatedInstr = 0;
            let importedGruppen = 0;
            let updatedGruppen = 0;
            
            // Import groups first
            if (data.gruppen && Array.isArray(data.gruppen)) {
                for (const gruppe of data.gruppen) {
                    try {
                        const existing = gruppen.find(g => g.id === gruppe.id);
                        if (existing) {
                            await updateInstrumentengruppe(gruppe.id, gruppe);
                            updatedGruppen++;
                        } else {
                            await createInstrumentengruppe(gruppe);
                            importedGruppen++;
                        }
                    } catch (err) {
                        console.error('Gruppe import error:', err);
                    }
                }
            }
            
            // Import instruments
            const instrumentList = data.instrumente || data;
            if (Array.isArray(instrumentList)) {
                for (const item of instrumentList) {
                    try {
                        const existing = instrumente.find(i => i.id === item.id);
                        if (existing) {
                            await updateInstrument(item.id, item);
                            updatedInstr++;
                        } else {
                            await createInstrument(item);
                            importedInstr++;
                        }
                    } catch (err) {
                        console.error('Instrument import error:', err);
                    }
                }
            }
            
            setJsonImportResult({ 
                imported: importedInstr + importedGruppen, 
                updated: updatedInstr + updatedGruppen, 
                errors: [] 
            });
            
            if (importedInstr + updatedInstr + importedGruppen + updatedGruppen > 0) {
                toast.success(`Instrumente: ${importedInstr} neu, ${updatedInstr} aktualisiert. Gruppen: ${importedGruppen} neu, ${updatedGruppen} aktualisiert.`);
                fetchData();
            }
            
            setJsonImportFile(null);
            if (jsonInputRef.current) jsonInputRef.current.value = '';
            
        } catch (err) {
            toast.error('Import fehlgeschlagen: ' + err.message);
            setJsonImportResult({ imported: 0, updated: 0, errors: [{ error: err.message }] });
        } finally {
            setJsonImporting(false);
        }
    };

    const closeDataDialog = () => {
        setDataDialogOpen(false);
        setJsonImportFile(null);
        setJsonImportResult(null);
        if (jsonInputRef.current) jsonInputRef.current.value = '';
    };

    // Handle drag end for reordering groups
    const handleDragEnd = async (event) => {
        const { active, over } = event;
        
        if (!over || active.id === over.id) return;
        
        const oldIndex = gruppen.findIndex(g => g.id === active.id);
        const newIndex = gruppen.findIndex(g => g.id === over.id);
        
        const newGruppen = arrayMove(gruppen, oldIndex, newIndex);
        setGruppen(newGruppen);
        
        // Save new order to backend
        try {
            const token = localStorage.getItem('auth_token');
            await axios.post(`${API_URL}/api/instrumentengruppen/reorder`, 
                newGruppen.map(g => g.id),
                { headers: { Authorization: `Bearer ${token}` }}
            );
            toast.success('Reihenfolge gespeichert');
        } catch (error) {
            toast.error('Fehler beim Speichern der Reihenfolge');
            fetchData(); // Reload on error
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Filter instruments based on search
    const filteredInstrumente = instrumente.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Helper: get gruppe_id from instrument
    const getGruppeId = (instrument) => instrument.gruppe_id;

    // Filter groups that have matching instruments OR match the search term themselves
    const filteredGruppen = gruppen.filter(gruppe => {
        const gruppeMatches = gruppe.name.toLowerCase().includes(searchTerm.toLowerCase());
        const hasMatchingInstruments = filteredInstrumente.some(i => getGruppeId(i) === gruppe.id);
        return gruppeMatches || hasMatchingInstruments || !searchTerm;
    });

    // Group instruments by Instrumentengruppe
    const groupedItems = filteredGruppen.map(gruppe => ({
        ...gruppe,
        instrumente: filteredInstrumente.filter(i => getGruppeId(i) === gruppe.id)
    }));

    const toggleGroup = (groupId) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupId)) {
            newExpanded.delete(groupId);
        } else {
            newExpanded.add(groupId);
        }
        setExpandedGroups(newExpanded);
    };

    const expandAll = () => setExpandedGroups(new Set(gruppen.map(g => g.id)));
    const collapseAll = () => setExpandedGroups(new Set());

    // Instrument handlers
    const handleOpenInstrumentDialog = (item = null) => {
        if (item) {
            setEditInstrument(item);
            setInstrumentFormData({ 
                name: item.name, 
                gruppe_id: item.gruppe_id,
                icon: item.icon || ''
            });
        } else {
            setEditInstrument(null);
            setInstrumentFormData({ name: '', gruppe_id: '', icon: '' });
        }
        setInstrumentDialogOpen(true);
    };

    const handleSaveInstrument = async () => {
        try {
            if (editInstrument) {
                await updateInstrument(editInstrument.id, instrumentFormData);
                toast.success('Instrument aktualisiert');
            } else {
                await createInstrument(instrumentFormData);
                toast.success('Instrument erstellt');
            }
            setInstrumentDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error(getErrorMessage(error, 'Fehler beim Speichern'));
        }
    };

    // Gruppe handlers
    const handleOpenGruppeDialog = (item = null) => {
        if (item) {
            setEditGruppe(item);
            setGruppeFormData({ name: item.name, icon: item.icon || '' });
        } else {
            setEditGruppe(null);
            setGruppeFormData({ name: '', icon: '' });
        }
        setGruppeDialogOpen(true);
    };

    const handleSaveGruppe = async () => {
        try {
            if (editGruppe) {
                await updateInstrumentengruppe(editGruppe.id, gruppeFormData);
                toast.success('Gruppe aktualisiert');
            } else {
                await createInstrumentengruppe(gruppeFormData);
                toast.success('Gruppe erstellt');
            }
            setGruppeDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error(getErrorMessage(error, 'Fehler beim Speichern'));
        }
    };

    // Delete handler
    const handleDelete = async () => {
        try {
            if (deleteType === 'instrument') {
                await deleteInstrument(deleteItem.id);
                toast.success('Instrument gelöscht');
            } else if (deleteType === 'gruppe') {
                await deleteInstrumentengruppe(deleteItem.id);
                toast.success('Gruppe gelöscht');
            }
            setDeleteItem(null);
            setDeleteType(null);
            fetchData();
        } catch (error) {
            toast.error(getErrorMessage(error, 'Fehler beim Löschen'));
        }
    };

    const openDeleteDialog = (item, type) => {
        setDeleteItem(item);
        setDeleteType(type);
    };

    return (
        <>
            <AdminHeader title="Instrumente">
                <div className="flex items-center gap-2">
                    <Button 
                        variant="outline" 
                        onClick={() => setDataDialogOpen(true)} 
                        className="rounded-xl"
                        data-testid="instrumente-data-btn"
                    >
                        <FolderSync className="h-4 w-4 mr-2" />
                        Import / Export
                    </Button>
                    <Button variant="outline" onClick={() => handleOpenGruppeDialog()} className="rounded-xl">
                        <Plus className="h-4 w-4 mr-2" />
                        Neue Gruppe
                    </Button>
                    <Button onClick={() => handleOpenInstrumentDialog()} className="rounded-xl">
                        <Plus className="h-4 w-4 mr-2" />
                        Neues Instrument
                    </Button>
                </div>
            </AdminHeader>

            <div className="admin-content">
                {/* Search and Controls */}
                <div className="flex flex-wrap items-center gap-4 mb-8">
                    <div className="search-wrapper flex-1 max-w-xl relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 transition-colors" />
                        <Input
                            placeholder="Instrument oder Gruppe suchen..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                            data-testid="instrument-search"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={expandAll} className="rounded-lg">
                            Alle öffnen
                        </Button>
                        <Button variant="outline" size="sm" onClick={collapseAll} className="rounded-lg">
                            Alle schließen
                        </Button>
                    </div>
                    <div className="text-base text-muted-foreground ml-auto">
                        {instrumente.length} Instrumente in {gruppen.length} Gruppen
                    </div>
                </div>

                {/* Grouped List */}
                {loading ? (
                    <div className="space-y-4">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="bg-card rounded-2xl border p-4 animate-pulse">
                                <div className="h-6 bg-muted rounded w-48 mb-3" />
                                <div className="h-4 bg-muted rounded w-32" />
                            </div>
                        ))}
                    </div>
                ) : groupedItems.length === 0 ? (
                    <div className="empty-state">
                        <Music className="empty-state-icon" />
                        <p className="empty-state-title">Keine Einträge gefunden</p>
                    </div>
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={groupedItems.map(g => g.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <div className="space-y-3">
                                {groupedItems.map((gruppe) => {
                                    const isExpanded = expandedGroups.has(gruppe.id);
                                    const instrumentCount = instrumente.filter(i => getGruppeId(i) === gruppe.id).length;
                                    
                                    return (
                                        <SortableGruppeItem
                                            key={gruppe.id}
                                            gruppe={gruppe}
                                            isExpanded={isExpanded}
                                            instrumentCount={instrumentCount}
                                            instrumente={gruppe.instrumente}
                                            onToggle={toggleGroup}
                                            onEdit={handleOpenGruppeDialog}
                                            onDelete={(g) => openDeleteDialog(g, 'gruppe')}
                                            onEditInstrument={handleOpenInstrumentDialog}
                                            onDeleteInstrument={(i) => openDeleteDialog(i, 'instrument')}
                                        />
                                    );
                                })}
                            </div>
                        </SortableContext>
                    </DndContext>
                )}
            </div>

            {/* Instrument Dialog */}
            <Dialog open={instrumentDialogOpen} onOpenChange={setInstrumentDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-xl">
                            {editInstrument ? 'Instrument bearbeiten' : 'Neues Instrument'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <div className="form-group">
                            <Label htmlFor="instrument-name" className="form-label">Name *</Label>
                            <Input
                                id="instrument-name"
                                value={instrumentFormData.name}
                                onChange={(e) => setInstrumentFormData({ ...instrumentFormData, name: e.target.value })}
                                placeholder="z.B. Querflöte"
                                className="form-input"
                                autoFocus
                            />
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Gruppe *</Label>
                            <Select 
                                value={instrumentFormData.gruppe_id} 
                                onValueChange={(value) => setInstrumentFormData({ ...instrumentFormData, gruppe_id: value })}
                            >
                                <SelectTrigger className="form-input">
                                    <SelectValue placeholder="Gruppe wählen..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {gruppen.map(g => (
                                        <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {/* Icon-Picker ausgeblendet - kann später wieder aktiviert werden
                        <div className="form-group">
                            <Label className="form-label">Icon</Label>
                            <div className="grid grid-cols-5 gap-2">
                                {AVAILABLE_ICONS.map(({ name, icon: Icon, label }) => (
                                    <button
                                        key={name}
                                        type="button"
                                        onClick={() => setInstrumentFormData({ ...instrumentFormData, icon: name })}
                                        className={cn(
                                            "p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1",
                                            instrumentFormData.icon === name 
                                                ? "border-violet-500 bg-violet-50" 
                                                : "border-gray-200 hover:border-gray-300"
                                        )}
                                        title={label}
                                    >
                                        <Icon className={cn(
                                            "h-5 w-5",
                                            instrumentFormData.icon === name ? "text-violet-600" : "text-gray-500"
                                        )} />
                                    </button>
                                ))}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {instrumentFormData.icon 
                                    ? AVAILABLE_ICONS.find(i => i.name === instrumentFormData.icon)?.label || instrumentFormData.icon
                                    : 'Standard-Icon wird verwendet'}
                            </p>
                        </div>
                        */}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setInstrumentDialogOpen(false)} className="rounded-xl">
                            Abbrechen
                        </Button>
                        <Button 
                            onClick={handleSaveInstrument} 
                            disabled={!instrumentFormData.name.trim() || !instrumentFormData.gruppe_id} 
                            className="rounded-xl"
                        >
                            {editInstrument ? 'Speichern' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Gruppe Dialog */}
            <Dialog open={gruppeDialogOpen} onOpenChange={setGruppeDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-xl">
                            {editGruppe ? 'Gruppe bearbeiten' : 'Neue Instrumentengruppe'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <div className="form-group">
                            <Label htmlFor="gruppe-name" className="form-label">Name *</Label>
                            <Input
                                id="gruppe-name"
                                value={gruppeFormData.name}
                                onChange={(e) => setGruppeFormData({ ...gruppeFormData, name: e.target.value })}
                                placeholder="z.B. Holzblasinstrumente"
                                className="form-input"
                                autoFocus
                            />
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Emoji</Label>
                            <div className="grid grid-cols-6 gap-2">
                                {AVAILABLE_GROUP_EMOJIS.map(({ name, label }) => (
                                    <button
                                        key={name}
                                        type="button"
                                        onClick={() => setGruppeFormData({ ...gruppeFormData, icon: name })}
                                        className={cn(
                                            "p-3 rounded-xl border-2 transition-all text-2xl",
                                            gruppeFormData.icon === name 
                                                ? "border-teal-500 bg-teal-50" 
                                                : "border-gray-200 hover:border-gray-300"
                                        )}
                                        title={label}
                                    >
                                        {name}
                                    </button>
                                ))}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {gruppeFormData.icon 
                                    ? AVAILABLE_GROUP_EMOJIS.find(e => e.name === gruppeFormData.icon)?.label || gruppeFormData.icon
                                    : '🎵 wird als Standard verwendet'}
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setGruppeDialogOpen(false)} className="rounded-xl">
                            Abbrechen
                        </Button>
                        <Button 
                            onClick={handleSaveGruppe} 
                            disabled={!gruppeFormData.name.trim()} 
                            className="rounded-xl"
                        >
                            {editGruppe ? 'Speichern' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteItem} onOpenChange={() => { setDeleteItem(null); setDeleteType(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl">
                            {deleteType === 'gruppe' ? 'Gruppe löschen?' : 'Instrument löschen?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-base">
                            Möchten Sie &quot;{deleteItem?.name}&quot; wirklich löschen?
                            {deleteType === 'gruppe' && (
                                <span className="block mt-2 text-amber-600">
                                    Achtung: Alle Instrumente dieser Gruppe müssen vorher entfernt oder verschoben werden.
                                </span>
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDelete} 
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl"
                        >
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Import/Export Dialog */}
            <Dialog open={dataDialogOpen} onOpenChange={closeDataDialog}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-xl flex items-center gap-2">
                            <Database className="h-5 w-5 text-primary" />
                            Instrumente Import / Export
                        </DialogTitle>
                        <DialogDescription>
                            Exportiere Instrumente und Gruppen als Backup oder importiere aus verschiedenen Quellen.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <Tabs defaultValue="export" value={dataDialogTab} onValueChange={setDataDialogTab} className="mt-4">
                        <TabsList className="grid w-full grid-cols-2 mb-4">
                            <TabsTrigger value="export" className="flex items-center gap-2">
                                <FileDown className="h-4 w-4" />
                                Exportieren
                            </TabsTrigger>
                            <TabsTrigger value="import" className="flex items-center gap-2">
                                <FileUp className="h-4 w-4" />
                                Importieren
                            </TabsTrigger>
                        </TabsList>
                        
                        {/* EXPORT TAB */}
                        <TabsContent value="export" className="space-y-4">
                            <div className="grid gap-4">
                                {/* JSON Backup Export */}
                                <div className="p-4 border rounded-xl hover:bg-slate-50 transition-colors">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-start gap-3">
                                            <div className="p-2 bg-emerald-100 rounded-lg">
                                                <Download className="h-5 w-5 text-emerald-600" />
                                            </div>
                                            <div>
                                                <p className="font-semibold">Vollständiges Backup (JSON)</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {instrumente.length} Instrumente und {gruppen.length} Gruppen inkl. IDs
                                                </p>
                                            </div>
                                        </div>
                                        <Button onClick={handleJSONExport} className="rounded-xl">
                                            Herunterladen
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* CSV Export */}
                                <div className="p-4 border rounded-xl hover:bg-slate-50 transition-colors">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-start gap-3">
                                            <div className="p-2 bg-blue-100 rounded-lg">
                                                <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                                            </div>
                                            <div>
                                                <p className="font-semibold">CSV-Export</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Für Excel/Tabellenkalkulation (nur Instrumente)
                                                </p>
                                            </div>
                                        </div>
                                        <Button variant="outline" onClick={handleCSVExport} className="rounded-xl">
                                            Herunterladen
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </TabsContent>
                        
                        {/* IMPORT TAB */}
                        <TabsContent value="import" className="space-y-6">
                            {/* JSON Restore */}
                            <div className="p-4 border rounded-xl">
                                <div className="flex items-start gap-3 mb-3">
                                    <div className="p-2 bg-emerald-100 rounded-lg">
                                        <Upload className="h-5 w-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <p className="font-semibold">Backup wiederherstellen (JSON)</p>
                                        <p className="text-sm text-muted-foreground">
                                            Aus zuvor exportiertem JSON-Backup. Bestehende Einträge werden aktualisiert.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="file"
                                        accept=".json"
                                        onChange={(e) => setJsonImportFile(e.target.files[0])}
                                        ref={jsonInputRef}
                                        className="flex-1 text-sm file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-primary file:text-primary-foreground file:cursor-pointer cursor-pointer"
                                    />
                                    <Button 
                                        onClick={handleJSONImport} 
                                        disabled={!jsonImportFile || jsonImporting}
                                        className="rounded-xl"
                                    >
                                        {jsonImporting ? 'Lädt...' : 'Importieren'}
                                    </Button>
                                </div>
                                {jsonImportResult && (
                                    <div className="mt-3">
                                        {(jsonImportResult.imported > 0 || jsonImportResult.updated > 0) ? (
                                            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-emerald-800">
                                                <CheckCircle2 className="h-5 w-5" />
                                                <span className="font-medium">Erfolgreich!</span>
                                                <span className="text-sm">{jsonImportResult.imported} neu, {jsonImportResult.updated} aktualisiert</span>
                                            </div>
                                        ) : jsonImportResult.errors?.length > 0 && (
                                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                                                <AlertCircle className="h-4 w-4 inline mr-2" />
                                                {jsonImportResult.errors.map(e => e.error).join(', ')}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>
                    
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={closeDataDialog} className="rounded-xl">
                            Schließen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
